import axios from 'axios'
import config from '../config'
import { getToken } from '../utils/sessionManagement'
import { message, notification } from 'antd'

notification.config({
  placement: 'top',
  duration: 3, // La notification disparaîtra après 5 secondes.
  top: 50, // Cela détermine la marge depuis le haut. Ajustez selon vos besoins.
})

const apiClient = axios.create({
  baseURL: config.URL_API_BACKEND,
})

const apiClient_WhithoutToken = axios.create({
  baseURL: config.URL_API_BACKEND,
})

apiClient.interceptors.request.use(function (config) {
  const token = getToken()
  if (token) {
    config.headers.Authorization = `Token ${token}`
  }
  return config
})

export const login = async (username, password) => {
  try {
    const response = await apiClient_WhithoutToken.post('/api/auth/login/', {
      username,
      password,
    })
    return response
  } catch (error) {
    throw error
  }
}

export const oidcLogin = async (token) => {
  try {
    const response = await apiClient_WhithoutToken.post('/api/oidc-auth/', {
      token: token,
    })
    return response
  } catch (error) {
    throw error
  }
}

export const downloadFile = async (filename) => {
  try {
    const response = await apiClient.get(`${filename}`, {
      responseType: 'blob',
    })

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
  } catch (error) {
    throw error
  }
}

export const Form_GenerateID = async () => {
  try {
    const response = await apiClient.get('/api/form/generate-id/')
    return response.data.unused_id
  } catch (error) {
    throw error
  }
}

export const Form_createForm = async (formData) => {
  try {
    const response = await apiClient.post('/api/Form/', formData)
    return response.data
  } catch (error) {
    throw error
  }
}

export const getUserTableState = async (formUrlCode) => {
  try {
    const response = await apiClient.get(
      `/api/get_user_table_state/${formUrlCode}/`
    )
    return response.data
  } catch (error) {
    throw error
  }
}

export const updateUserTableState = async (formData) => {
  try {
    const response = await apiClient.post(
      '/api/update_user_table_state/',
      formData
    )
    return response.data
  } catch (error) {
    throw error
  }
}

export const Form_getFormDetails = async (url_code) => {
  try {
    const response = await apiClient.get(`/api/form_byurl/${url_code}/`)
    return response.data
  } catch (error) {
    throw error
  }
}

export const Fields_getALL = async (url_code) => {
  try {
    const response = await apiClient.get(`/api/form/${url_code}/fields/`)
    return response.data.fields
  } catch (error) {
    throw error
  }
}

export const deleteFormResponses = async (ids) => {
  try {
    // Assurer que ids est toujours un tableau
    if (!Array.isArray(ids)) {
      ids = [ids]
    }

    // Utiliser l'endpoint 'bulk_delete' pour la suppression
    const response = await apiClient.post(`/api/FormResponse/bulk_delete/`, {
      ids,
    })

    notification.success({ message: 'Données supprimées avec succès' })
    return response.data
  } catch (error) {
    console.error('Erreur lors de la suppression :', error)
    if (error.response && error.response.status === 403) {
      notification.error({
        message: "Vous n'êtes pas autorisé à effectuer cette action.",
      })
    } else {
      notification.error({
        message: 'Erreur lors de la suppression.',
      })
    }

    throw error
  }
}

export const duplicateFormResponse = async (id) => {
  try {
    const response = await apiClient.post(`/api/duplicate_response/${id}/`)
    notification.success({ message: 'Données dupliquées avec succès' })
    return response.data
  } catch (error) {
    console.error('Erreur lors de la duplication :', error)

    const defaultErrorMessage = 'Erreur lors de la duplication.'
    const errorMessage = error.response?.data?.error || defaultErrorMessage

    if (error.response?.status === 403) {
      notification.error({
        message: "Vous n'êtes pas autorisé à effectuer cette action.",
      })
    } else if (error.response?.data?.error_code !== 'AXIS_KEY_CONFLICT') {
      notification.error({
        message: errorMessage,
      })
    }

    throw error
  }
}

export const ViewForm = async (url_code, isEditing, idResponse) => {
  try {
    let apiUrl = `/api/viewform/${url_code}/`

    if (isEditing) {
      apiUrl += `edit/${idResponse}/`
    }

    const response = await apiClient.get(apiUrl)
    return response.data
  } catch (error) {
    throw error
  }
}

export const WorkspacesAPI = async (page = 1, filters) => {
  try {
    const response = await apiClient.get(`/api/workspaces/?page=${page}`, {
      params: filters,
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const TemplatesAPI = async (page = 1) => {
  try {
    const response = await apiClient.get(`/api/templatesList/?page=${page}`, {})
    return response.data
  } catch (error) {
    throw error
  }
}

export const searchUsers = async (searchQuery, abortSignal) => {
  try {
    const response = await apiClient.get(
      `/api/users_ldap/?search=${searchQuery}`,
      { cancelToken: abortSignal }
    )
    return response.data
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Request canceled', error.message)
    } else {
      console.error("Erreur lors de la recherche d'utilisateurs: ", error)
    }
    return []
  }
}

export const getFormCollaborators = async (url_code) => {
  try {
    const response = await apiClient.get(
      `/api/FormCollaborator/?search=${url_code}`
    )
    return response.data.results
  } catch (error) {
    throw error
  }
}

export const collectFormAnswersData = async (
  url_code,
  page = 1,
  page_size = 10,
  all_data = false,
  sortId = null,
  sortOrder = 'asc',
  search = '',
  columnSearch = {},
  includeLabels = false // Nouveau paramètre
) => {
  try {
    const params = new URLSearchParams()

    if (all_data) {
      params.append('all_data', 'true')
    } else {
      params.append('page', page)
      params.append('page_size', page_size)
      params.append('search', search)

      if (sortId && sortOrder) {
        params.append('sortId', sortId)
        params.append('sortOrder', sortOrder)
      }

      const validFilters = Object.fromEntries(
        Object.entries(columnSearch).filter(
          ([_, value]) => value && value !== ''
        )
      )

      if (Object.keys(validFilters).length) {
        params.append('filters', JSON.stringify(validFilters))
      }
    }

    // Ajouter le paramètre 'with_labels' si nécessaire
    if (includeLabels) {
      params.append('with_labels', 'true')
    }

    const endpoint = `/api/collectformdata/${url_code}/?${params.toString()}`
    const response = await apiClient.get(endpoint)
    return response.data
  } catch (error) {
    throw error
  }
}

export const updateFieldsAPI = async (formData) => {
  try {
    const response = await apiClient.post('/api/fields-save/', formData)
    return response.data
  } catch (error) {
    console.error('Erreur lors de la mise à jour :', error)

    if (error.response && error.response.status === 403) {
      notification.error({
        message: "Vous n'êtes pas autorisé à effectuer cette action.",
      })
    } else {
      notification.error({
        message: 'Erreur lors de la mise à jour.',
      })
    }

    throw error
  }
}

export const handleFormResponse = async (formData) => {
  try {
    const formDataObj = new FormData()
    Object.keys(formData).forEach((key) => {
      if (key === 'fields_values') {
        formDataObj.append(key, JSON.stringify(formData[key]))
        // Traiter les valeurs des champs
        formData[key].forEach((field) => {
          if (field.fieldType === 'Upload' && field.value instanceof File) {
            console.log('Adding file to FormData:', field.value)
            formDataObj.append(field.fieldId, field.value, field.value.name)
          } else {
            // Pour les autres types de champs, ajoutez la valeur telle quelle
            formDataObj.append(field.fieldId, field.value)
          }
        })
      } else {
        // Pour toutes les autres clés, ajoutez-les telles quelles à formDataObj
        formDataObj.append(key, formData[key])
      }
    })

    const response = await apiClient.post(
      '/api/handle_form_response/',
      formDataObj,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  } catch (error) {
    console.error('Erreur lors de la mise à jour :', error)
    // Si l'erreur provient de la réponse HTTP
    if (error.response) {
      // Si le code d'erreur est 403
      if (error.response.status === 403) {
        notification.error({
          message: "Vous n'êtes pas autorisé à effectuer cette action.",
        })
      } else {
        // Utilisez le contenu de la réponse pour afficher le message d'erreur
        notification.error({
          message: 'Error',
          description: error.response.data.error,
        })
      }
    } else {
      // Si l'erreur ne provient pas d'une réponse HTTP
      notification.error({
        message: 'Error',
        description: error.message || 'Une erreur est survenue !',
      })
    }

    throw error
  }
}
export const updateFormAPI = async (formData, url_code) => {
  try {
    const response = await apiClient.post(
      `/api/form/${url_code}/save/`,
      formData
    )
    return response.data
  } catch (error) {
    console.error('Erreur lors de la mise à jour :', error)

    if (error.response && error.response.status === 403) {
      notification.error({
        message: "Vous n'êtes pas autorisé à effectuer cette action.",
      })
    } else {
      notification.error({
        message: 'Erreur lors de la mise à jour.',
      })
    }

    throw error
  }
}

export const duplicateFormAPI = async (url_code) => {
  try {
    const response = await apiClient.post('/api/duplicate_form/', { url_code })
    notification.success({ message: 'Formulaire dupliqué avec succès.' })
    return response.data
  } catch (error) {
    console.error('Erreur lors de la duplication :', error)

    if (error.response && error.response.status === 403) {
      notification.error({
        message: "Vous n'êtes pas autorisé à effectuer cette action.",
      })
    } else {
      notification.error({
        message: 'Erreur lors de la duplication.',
      })
    }

    throw error
  }
}

export const CreateFormFromTemplate = async (url_code) => {
  try {
    const response = await apiClient.post('/api/create_form_from_template/', {
      url_code,
    })
    notification.success({ message: 'Formulaire créé avec succès.' })
    return response.data
  } catch (error) {
    console.error('Erreur lors de la création :', error)

    if (error.response && error.response.status === 403) {
      notification.error({
        message: "Vous n'êtes pas autorisé à effectuer cette action.",
      })
    } else {
      notification.error({
        message: 'Erreur lors de la création.',
      })
    }

    throw error
  }
}

export const deleteFormAPI = async (url_code) => {
  try {
    const response = await apiClient.post('/api/delete_form/', { url_code })
    notification.success({ message: 'Formulaire supprimé avec succès.' })
    return response.data
  } catch (error) {
    console.error('Erreur lors de la suppression :', error)

    if (error.response && error.response.status === 403) {
      notification.error({
        message: "Vous n'êtes pas autorisé à effectuer cette action.",
      })
    } else {
      notification.error({
        message: 'Erreur lors de la suppression.',
      })
    }

    throw error
  }
}

export const addCollaboratorAPI = async (formData) => {
  try {
    const response = await apiClient.post('/api/FormCollaborator/', formData)
    notification.success({ message: 'Utilisateur ajouté avec succès' })
    return response.data
  } catch (error) {
    const errorMessage =
      error.response && error.response.data && error.response.data.detail
    if (errorMessage === 'COLLABORATOR_ALREADY_EXISTS') {
      notification.error({
        message: 'Ce collaborateur existe déja.',
      })
    } else {
      notification.error({
        message: "Erreur lors de l'ajout du collaborateur.",
      })
      console.error("Erreur lors de l'ajout du collaborateur :", error)
    }
    throw error
  }
}

export const updateCollaborator = async (formData, id) => {
  try {
    const response = await apiClient.put(
      `/api/FormCollaborator/${id}/`,
      formData
    )
    return response.data
  } catch (error) {
    throw error
  }
}

export const deleteCollaborator = async (id) => {
  try {
    const response = await apiClient.delete(`/api/FormCollaborator/${id}/`)
    return response.data
  } catch (error) {
    throw error
  }
}

// View API pour ajouter une personne des utilisateurs qui peuvent répondre à un formulaire
export const addRestrictedSubmissions = async (formData) => {
  try {
    const response = await apiClient.post(
      '/api/FormRestrictedSubmissionsGroup/',
      formData
    )
    notification.success({ message: 'Utilisateur ajouté avec succès' })
    return response.data
  } catch (error) {
    notification.error({
      message: "Erreur lors de l'ajout du collaborateur.",
    })
    console.error("Erreur lors de l'ajout du collaborateur :", error)
    throw error
  }
}

// View API pour retirer une personne des utilisateurs qui peuvent répondre à un formulaire
export const deleteRestrictedSubmissions = async (id) => {
  try {
    const response = await apiClient.delete(
      `/api/FormRestrictedSubmissionsGroup/${id}/`
    )
    return response.data
  } catch (error) {
    throw error
  }
}

// View API pour modifier une personne des utilisateurs qui peuvent répondre à un formulaire
export const updateRestrictedSubmissions = async (formData, id) => {
  try {
    const response = await apiClient.put(
      `/api/FormRestrictedSubmissionsGroup/${id}/`,
      formData
    )
    return response.data
  } catch (error) {
    throw error
  }
}

// View API pour ajouter une personne des utilisateurs qui peuvent recevoir le mail
export const addEmailNotificationsGroup = async (formData) => {
  try {
    const response = await apiClient.post(
      '/api/FormEmailNotificationsGroup/',
      formData
    )
    notification.success({ message: 'Utilisateur ajouté avec succès' })
    return response.data
  } catch (error) {
    notification.error({
      message: "Erreur lors de l'ajout du collaborateur.",
    })
    console.error("Erreur lors de l'ajout du collaborateur :", error)
    throw error
  }
}

// View API pour retirer une personne des utilisateurs qui peuvent recevoir le mail
export const deleteEmailNotificationsGroup = async (id) => {
  try {
    const response = await apiClient.delete(
      `/api/FormEmailNotificationsGroup/${id}/`
    )
    return response.data
  } catch (error) {
    throw error
  }
}

// View API pour modifier une personne des utilisateurs qui peuvent recevoir le mail
export const updateEmailNotificationsGroup = async (formData, id) => {
  try {
    const response = await apiClient.put(
      `/api/FormEmailNotificationsGroup/${id}/`,
      formData
    )
    return response.data
  } catch (error) {
    throw error
  }
}
